import { formatDate } from '@frontend/mwm-ui'

import type { DurationUnitType } from 'dayjs/plugin/duration'

import dayjs from './dayjs'

export const dateComparator = (date1: number, date2: number): number => {
  if (date1 === date2) return 0
  return date1 > date2 ? 1 : -1
}

export const dateSort = (date1: number, date2: number, order: 'asc' | 'desc' = 'asc'): number => {
  if (date1 === date2) return 0
  if (order === 'asc') return date1 > date2 ? 1 : -1
  return date1 > date2 ? -1 : 1
}

export const dateFilterComparator = (filterLocalDateAtMidnight: number, cellValue: number): number => {
  const selectedValue = dayjs(filterLocalDateAtMidnight).format('YYYY/MM/DD')
  const cellDisplayedValue = dayjs(cellValue / 1000000).format('YYYY/MM/DD')

  if (cellDisplayedValue < selectedValue) {
    return -1
  } else if (cellDisplayedValue > selectedValue) {
    return 1
  }

  return 0
}

export const strDateFilterComparator = (filterLocalDateAtMidnight: string, cellValue: string): number => {
  const selectedValue = formatDate(filterLocalDateAtMidnight, 'YYYY/MM/DD')
  const cellDisplayedValue = formatDate(cellValue, 'YYYY/MM/DD')

  if (cellDisplayedValue < selectedValue) {
    return -1
  } else if (cellDisplayedValue > selectedValue) {
    return 1
  }

  return 0
}

export const getRelativeDate = (date: number): string => {
  return dayjs(date / 1000000).fromNow()
}

export const formatDuration = (duration: number, unit: DurationUnitType): string => {
  const getSubString = (value: number, unit: string) => {
    if (!value) return ''
    return `${value} ${unit}${value > 1 ? 's' : ''}`
  }

  const durationBreakdown = dayjs.duration(duration, unit)
  return `${getSubString(durationBreakdown.years(), 'year')} ${getSubString(
    durationBreakdown.months(),
    'month'
  )} ${getSubString(durationBreakdown.days(), 'day')}`
}

/**
 * It returns all the dates between a start date and an end date.
 * @param startDate: YYYY-MM-DD. It must be before or equal to end date.
 * @param endDate: YYYY-MM-DD
 * @return an array of YYYY-MM-DD dates
 */
export const getDatesBetween = (startDate: string, endDate: string): string[] => {
  const datesArray = []

  let start = dayjs(startDate)
  const end = dayjs(endDate)

  if (start.isAfter(end)) {
    throw new Error('Start date must be before or equal to end date')
  }

  while (start.isBefore(end) || start.isSame(end)) {
    datesArray.push(start.format('YYYY-MM-DD'))
    start = start.add(1, 'day')
  }

  return datesArray
}

/** It takes a timestamp and returns it in milliseconds (13 digits)  */
export const convertToMilliseconds = (timestamp: number): number => {
  const length = timestamp.toString().length

  switch (length) {
    case 19:
      return Math.floor(timestamp / 1_000_000)
    case 10:
      return timestamp * 1_000
    default:
      return timestamp
  }
}
